import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { authHeader } from '../../services/authHeader';
import styles from './InviteNotification.module.css';

const InviteNotification = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const accessToken = useSelector((state) => state.auth.accessToken);
    const [pendingInvites, setPendingInvites] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(false);

    const isAuthRoute =
        pathname.startsWith('/login') ||
        pathname.startsWith('/createuser') ||
        pathname.startsWith('/forgotpassword') ||
        pathname.startsWith('/resetpassword');

    const isBlocked =
        isAuthRoute ||
        pathname.startsWith('/lobby') ||
        pathname.startsWith('/scoreboard');

    const fetchStatus = useCallback(async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/g/lobby/status/`, authHeader());
            const data = res.data;

            if (data.type === 'pending_invites') {
                setPendingInvites(data.invites);
                setCurrentIndex(0);
            } else {
                setPendingInvites([]);
                if (data.type === 'active_lobby' || data.type === 'created_lobby') {
                    navigate(`/lobby/${data.lobbyID}`);
                } else if (data.type === 'active_game') {
                    navigate(`/scoreboard/${data.gameID}`);
                }
            }
        } catch (err) {
            console.error('Error fetching lobby status:', err);
        }
    }, [navigate]);

    useEffect(() => {
        if (!accessToken || isBlocked) return;
        fetchStatus();
        const interval = setInterval(fetchStatus, 5000);
        return () => clearInterval(interval);
    }, [fetchStatus, accessToken, isBlocked]);

    const currentInvite = pendingInvites[currentIndex];

    const handleAccept = async () => {
        if (!currentInvite) return;
        setLoading(true);
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/g/invites/respond/`,
                { inviteID: currentInvite.inviteID, action: 'accept' },
                authHeader()
            );
            navigate(`/lobby/${res.data.lobbyID}`);
        } catch (err) {
            console.error('Error accepting invite:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleReject = async () => {
        if (!currentInvite) return;
        setLoading(true);
        try {
            await axios.post(
                `${process.env.REACT_APP_API_URL}/api/g/invites/respond/`,
                { inviteID: currentInvite.inviteID, action: 'reject' },
                authHeader()
            );
            const remaining = pendingInvites.filter((_, i) => i !== currentIndex);
            setPendingInvites(remaining);
            setCurrentIndex(0);
        } catch (err) {
            console.error('Error rejecting invite:', err);
        } finally {
            setLoading(false);
        }
    };

    if (isBlocked || !currentInvite) return null;

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <h2 className={styles.title}>Game Invite</h2>
                <p className={styles.from}>
                    <span className={styles.sender}>{currentInvite.sender}</span> invited you to a game
                </p>
                <p className={styles.team}>
                    Team: <strong>{currentInvite.team}</strong>
                </p>
                {pendingInvites.length > 1 && (
                    <p className={styles.count}>{pendingInvites.length} invite{pendingInvites.length > 1 ? 's' : ''} pending</p>
                )}
                <div className={styles.actions}>
                    <button className={styles.rejectBtn} onClick={handleReject} disabled={loading}>
                        Decline
                    </button>
                    <button className={styles.acceptBtn} onClick={handleAccept} disabled={loading}>
                        Accept
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InviteNotification;
